import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ResponsiveContainer, 
  ComposedChart, 
  Line, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  PieChart, 
  Pie, 
  Cell 
} from 'recharts';
import { 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Percent, 
  ShoppingBag, 
  Users, 
  Warehouse, 
  Calendar, 
  Bell 
} from 'lucide-react';
import { toast } from 'sonner';

import API from '../api_config';

const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

function formatCLP(amount) {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0
  }).format(amount);
}

export default function FinancialDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [period, setPeriod] = useState('30'); // last 30 days default
  
  // Profit Analysis data
  const [productProfit, setProductProfit] = useState([]);
  const [categoryProfit, setCategoryProfit] = useState([]);
  const [customerProfit, setCustomerProfit] = useState([]);
  const [warehouseProfit, setWarehouseProfit] = useState([]);
  const [periodProfit, setPeriodProfit] = useState([]);

  const fetchFinancialData = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const startDate = new Date();
      startDate.setDate(now.getDate() - parseInt(period));
      
      const startStr = startDate.toISOString();
      const endStr = now.toISOString();

      const [statsRes, alertsRes, prodRes, catRes, custRes, whRes, timeRes] = await Promise.all([
        axios.get(`${API}/dashboard/financial-stats?start_date=${startStr}&end_date=${endStr}`),
        axios.get(`${API}/notifications/smart-alerts`),
        axios.get(`${API}/dashboard/profit-analysis?dimension=product&start_date=${startStr}&end_date=${endStr}`),
        axios.get(`${API}/dashboard/profit-analysis?dimension=category&start_date=${startStr}&end_date=${endStr}`),
        axios.get(`${API}/dashboard/profit-analysis?dimension=customer&start_date=${startStr}&end_date=${endStr}`),
        axios.get(`${API}/dashboard/profit-analysis?dimension=warehouse&start_date=${startStr}&end_date=${endStr}`),
        axios.get(`${API}/dashboard/profit-analysis?dimension=period&start_date=${startStr}&end_date=${endStr}`)
      ]);

      setStats(statsRes.data);
      setAlerts(alertsRes.data);
      setProductProfit(prodRes.data);
      setCategoryProfit(catRes.data);
      setCustomerProfit(custRes.data);
      setWarehouseProfit(whRes.data);
      setPeriodProfit(timeRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar datos financieros del dashboard');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchFinancialData();
  }, [fetchFinancialData]);

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-muted-foreground">Cargando analíticas financieras...</div>
      </div>
    );
  }

  // Categories distribution for PieChart
  const pieData = categoryProfit.map(item => ({
    name: item.label,
    value: item.profit
  }));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">POS Inteligente Financiero</h1>
          <p className="text-muted-foreground mt-1">Control integral de rentabilidad, gastos e inventario</p>
        </div>
        <div className="flex items-center gap-2 bg-muted p-1 rounded-lg">
          <Button 
            variant={period === '7' ? 'default' : 'ghost'} 
            size="sm" 
            onClick={() => setPeriod('7')}
          >
            7 Días
          </Button>
          <Button 
            variant={period === '30' ? 'default' : 'ghost'} 
            size="sm" 
            onClick={() => setPeriod('30')}
          >
            30 Días
          </Button>
          <Button 
            variant={period === '90' ? 'default' : 'ghost'} 
            size="sm" 
            onClick={() => setPeriod('90')}
          >
            90 Días
          </Button>
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Sales */}
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Ventas (Netas)</CardTitle>
            <DollarSign className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono tracking-tight">{formatCLP(stats?.total_sales || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Ingresos brutos por boletas/facturas</p>
          </CardContent>
        </Card>

        {/* Gross Profit */}
        <Card className="hover:shadow-md transition-shadow bg-emerald-50/40 dark:bg-emerald-950/10 border-emerald-200 dark:border-emerald-900">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-emerald-800 dark:text-emerald-300">Utilidad Bruta</CardTitle>
            <TrendingUp className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono tracking-tight text-emerald-700 dark:text-emerald-400">
              {formatCLP(stats?.gross_profit || 0)}
            </div>
            <p className="text-xs text-emerald-600/80 dark:text-emerald-400/80 mt-1">
              Margen de ventas: {stats?.total_sales ? round((stats.gross_profit / stats.total_sales) * 100, 1) : 0}%
            </p>
          </CardContent>
        </Card>

        {/* Total Expenses */}
        <Card className="hover:shadow-md transition-shadow bg-red-50/40 dark:bg-red-950/10 border-red-200 dark:border-red-900">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-red-800 dark:text-red-300">Egresos / Gastos</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono tracking-tight text-red-700 dark:text-red-400">
              {formatCLP(stats?.total_expenses || 0)}
            </div>
            <p className="text-xs text-red-600/80 dark:text-red-400/80 mt-1">Costos operativos de la empresa</p>
          </CardContent>
        </Card>

        {/* Net Profit */}
        <Card className="hover:shadow-md transition-shadow bg-indigo-50/40 dark:bg-indigo-950/10 border-indigo-200 dark:border-indigo-900">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-indigo-800 dark:text-indigo-300">Utilidad Neta</CardTitle>
            <Percent className="h-4 w-4 text-indigo-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono tracking-tight text-indigo-700 dark:text-indigo-400">
              {formatCLP(stats?.net_profit || 0)}
            </div>
            <p className="text-xs text-indigo-600/80 dark:text-indigo-400/80 mt-1">Rendimiento real del negocio</p>
          </CardContent>
        </Card>
      </div>

      {/* Secondary KPIs Grid (Financial Health alerts indicators) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Accounts Receivable */}
        <Card className="bg-orange-50/20 dark:bg-orange-950/5">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-medium">Por Cobrar</span>
              <ShoppingBag className="h-4 w-4 text-orange-500" />
            </div>
            <div className="text-xl font-bold font-mono mt-1 text-orange-600 dark:text-orange-400">
              {formatCLP(stats?.accounts_receivable || 0)}
            </div>
          </CardContent>
        </Card>

        {/* Critical Stock */}
        <Card className={stats?.critical_stock_count > 0 ? "bg-red-50/20 dark:bg-red-950/5 border-red-200" : ""}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-medium">Stock Crítico</span>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </div>
            <div className={`text-xl font-bold mt-1 ${stats?.critical_stock_count > 0 ? 'text-red-500' : ''}`}>
              {stats?.critical_stock_count || 0} prod.
            </div>
          </CardContent>
        </Card>

        {/* Low Margin Items */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-medium">Bajo Margen (&lt;15%)</span>
              <Percent className="h-4 w-4 text-yellow-600" />
            </div>
            <div className="text-xl font-bold mt-1 text-yellow-600">
              {stats?.low_margin_count || 0} prod.
            </div>
          </CardContent>
        </Card>

        {/* Cash register discrepancies */}
        <Card className={stats?.caja_descuadrada_count > 0 ? "bg-red-50/20 dark:bg-red-950/5 border-red-200" : ""}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-medium">Cajas Descuadradas</span>
              <AlertTriangle className="h-4 w-4 text-red-500" />
            </div>
            <div className={`text-xl font-bold mt-1 ${stats?.caja_descuadrada_count > 0 ? 'text-red-500' : ''}`}>
              {stats?.caja_descuadrada_count || 0} sesiones
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Charts & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Trend Chart (Sales, Profit, Cost) */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Historial de Rendimiento</CardTitle>
            <CardDescription>Comparativa de Ventas netas y Utilidades en el período</CardDescription>
          </CardHeader>
          <CardContent className="h-80">
            {periodProfit.length === 0 ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">No hay ventas registradas</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={periodProfit}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="label" fontSize={11} tickLine={false} />
                  <YAxis fontSize={11} tickLine={false} />
                  <Tooltip formatter={(value) => formatCLP(value)} />
                  <Legend verticalAlign="top" height={36} />
                  <Bar dataKey="sales" name="Ventas" fill="#6366f1" radius={[4, 4, 0, 0]} opacity={0.8} />
                  <Line type="monotone" dataKey="profit" name="Utilidad Real" stroke="#10b981" strokeWidth={2.5} dot={{ r: 4 }} />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* Smart Alerts Pane */}
        <Card>
          <CardHeader className="flex flex-row items-center gap-2 pb-3">
            <Bell className="h-5 w-5 text-indigo-600" />
            <div>
              <CardTitle>Alertas Financieras Inteligentes</CardTitle>
              <CardDescription>Monitoreo automático del negocio</CardDescription>
            </div>
          </CardHeader>
          <CardContent className="h-[300px] overflow-y-auto space-y-3 custom-scrollbar pr-2">
            {alerts.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground text-sm">
                <Bell className="h-8 w-8 opacity-30 mb-2" />
                <span>Tu negocio está en orden. Sin alertas.</span>
              </div>
            ) : (
              alerts.map(alert => (
                <div 
                  key={alert.id}
                  className={`p-3 rounded-xl border flex gap-3 text-sm transition-all ${
                    alert.severity === 'high' 
                      ? 'bg-red-50/50 dark:bg-red-950/10 border-red-200 dark:border-red-950' 
                      : 'bg-yellow-50/30 dark:bg-yellow-950/5 border-yellow-100 dark:border-yellow-950'
                  }`}
                >
                  <AlertTriangle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${alert.severity === 'high' ? 'text-red-500' : 'text-yellow-600'}`} />
                  <div>
                    <h4 className="font-semibold">{alert.title}</h4>
                    <p className="text-muted-foreground text-xs mt-0.5">{alert.message}</p>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs with dimensions analysis */}
      <Card>
        <CardHeader>
          <CardTitle>Análisis de Rentabilidad</CardTitle>
          <CardDescription>Desglose detallado de ventas y márgenes por dimensión de negocio</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="products" className="space-y-4">
            <TabsList>
              <TabsTrigger value="products">Productos</TabsTrigger>
              <TabsTrigger value="categories">Categorías</TabsTrigger>
              <TabsTrigger value="customers">Clientes</TabsTrigger>
              <TabsTrigger value="warehouses">Bodegas</TabsTrigger>
            </TabsList>

            {/* Products Tab */}
            <TabsContent value="products" className="space-y-4">
              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase bg-muted text-muted-foreground">
                    <tr>
                      <th className="px-6 py-3">Producto</th>
                      <th className="px-6 py-3 text-right">Ventas Totales</th>
                      <th className="px-6 py-3 text-right">Costo Total</th>
                      <th className="px-6 py-3 text-right text-emerald-600 font-bold">Utilidad Real</th>
                      <th className="px-6 py-3 text-right">Margen (%)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {productProfit.map((item, idx) => (
                      <tr key={idx} className="hover:bg-muted/50">
                        <td className="px-6 py-4 font-medium">{item.label}</td>
                        <td className="px-6 py-4 text-right font-mono">{formatCLP(item.sales)}</td>
                        <td className="px-6 py-4 text-right font-mono">{formatCLP(item.cost)}</td>
                        <td className="px-6 py-4 text-right font-mono text-emerald-600 font-bold">{formatCLP(item.profit)}</td>
                        <td className="px-6 py-4 text-right">
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            item.margin >= 30 ? 'bg-emerald-100 text-emerald-800' :
                            item.margin >= 15 ? 'bg-indigo-100 text-indigo-800' : 'bg-red-100 text-red-800'
                          }`}>{item.margin}%</span>
                        </td>
                      </tr>
                    ))}
                    {productProfit.length === 0 && (
                      <tr>
                        <td colSpan="5" className="text-center py-8 text-muted-foreground">No hay ventas registradas</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Categories Tab */}
            <TabsContent value="categories" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 overflow-x-auto border rounded-xl h-fit">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase bg-muted text-muted-foreground">
                    <tr>
                      <th className="px-6 py-3">Categoría</th>
                      <th className="px-6 py-3 text-right">Ventas</th>
                      <th className="px-6 py-3 text-right text-emerald-600">Utilidad</th>
                      <th className="px-6 py-3 text-right">Margen</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {categoryProfit.map((item, idx) => (
                      <tr key={idx} className="hover:bg-muted/50">
                        <td className="px-6 py-4 font-medium">{item.label}</td>
                        <td className="px-6 py-4 text-right font-mono">{formatCLP(item.sales)}</td>
                        <td className="px-6 py-4 text-right font-mono text-emerald-600 font-semibold">{formatCLP(item.profit)}</td>
                        <td className="px-6 py-4 text-right font-semibold">{item.margin}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="border rounded-xl p-4 flex flex-col justify-center items-center h-80">
                <h4 className="text-sm font-semibold mb-4 text-muted-foreground text-center">Distribución de Utilidad</h4>
                {pieData.length === 0 ? (
                  <div className="text-muted-foreground text-sm">Sin datos</div>
                ) : (
                  <ResponsiveContainer width="100%" height="80%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={80}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => formatCLP(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
                <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center mt-2 text-xs">
                  {pieData.map((item, index) => (
                    <div key={index} className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }}></span>
                      <span className="text-muted-foreground">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Customers Tab */}
            <TabsContent value="customers" className="space-y-4">
              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase bg-muted text-muted-foreground">
                    <tr>
                      <th className="px-6 py-3">Cliente</th>
                      <th className="px-6 py-3 text-right">Ventas Realizadas</th>
                      <th className="px-6 py-3 text-right text-emerald-600 font-bold">Utilidad Bruta</th>
                      <th className="px-6 py-3 text-right">Margen Promedio</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {customerProfit.map((item, idx) => (
                      <tr key={idx} className="hover:bg-muted/50">
                        <td className="px-6 py-4 font-medium">{item.label}</td>
                        <td className="px-6 py-4 text-right font-mono">{formatCLP(item.sales)}</td>
                        <td className="px-6 py-4 text-right font-mono text-emerald-600 font-bold">{formatCLP(item.profit)}</td>
                        <td className="px-6 py-4 text-right font-semibold">{item.margin}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>

            {/* Warehouses Tab */}
            <TabsContent value="warehouses" className="space-y-4">
              <div className="overflow-x-auto border rounded-xl">
                <table className="w-full text-sm text-left">
                  <thead className="text-xs uppercase bg-muted text-muted-foreground">
                    <tr>
                      <th className="px-6 py-3">Bodega</th>
                      <th className="px-6 py-3 text-right">Ventas</th>
                      <th className="px-6 py-3 text-right text-emerald-600 font-bold">Utilidad Generada</th>
                      <th className="px-6 py-3 text-right">Margen Bodega</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {warehouseProfit.map((item, idx) => (
                      <tr key={idx} className="hover:bg-muted/50">
                        <td className="px-6 py-4 font-medium">{item.label}</td>
                        <td className="px-6 py-4 text-right font-mono">{formatCLP(item.sales)}</td>
                        <td className="px-6 py-4 text-right font-mono text-emerald-600 font-bold">{formatCLP(item.profit)}</td>
                        <td className="px-6 py-4 text-right font-semibold">{item.margin}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function round(value, decimals) {
  return Number(Math.round(value + 'e' + decimals) + 'e-' + decimals);
}
